import { W, H, COLS, CELL, BOARD_X, BOARD_W, BOARD_Y, BOARD_H, Phase, Button } from './types';
import { getButtons, getPauseButtons } from './renderer';

export interface InputHandlers {
  getPhase: () => Phase;
  canInput: () => boolean;
  isMenuOpen: () => boolean;
  hoverCol: (col: number) => void;   // desktop mouse hover over the board
  dragCol: (col: number) => void;    // pointer held & moving
  release: (col: number | null) => void; // pointerup: col to drop, or null = cancel
  pressButton: (name: string) => void;
  keyMove: (dir: number) => void;    // ← / →
  keyDrop: () => void;               // ↓ / Space / Enter
  keyRestart: () => void;            // R
}

const inside = (b: Button, x: number, y: number) => x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;
const colAt = (x: number) => Math.max(0, Math.min(COLS - 1, Math.floor((x - BOARD_X) / CELL)));
const overBoard = (x: number, y: number) =>
  x >= BOARD_X && x <= BOARD_X + BOARD_W && y >= 80 && y <= BOARD_Y + BOARD_H;
const canHover = window.matchMedia('(hover: hover)').matches;

export function attachInput(canvas: HTMLCanvasElement, h: InputHandlers): void {
  const toLogical = (cx: number, cy: number) => {
    const r = canvas.getBoundingClientRect();
    return { x: (cx - r.left) * (W / r.width), y: (cy - r.top) * (H / r.height) };
  };

  const hitButton = (x: number, y: number): boolean => {
    const btns = h.isMenuOpen() ? getPauseButtons() : getButtons(h.getPhase());
    for (const name in btns) {
      if (inside(btns[name], x, y)) { h.pressButton(name); return true; }
    }
    return false;
  };

  let pressing = false;

  canvas.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    const { x, y } = toLogical(e.clientX, e.clientY);
    if (hitButton(x, y)) { pressing = false; return; }
    if (h.canInput() && overBoard(x, y)) {
      pressing = true;
      h.dragCol(colAt(x));
      canvas.setPointerCapture(e.pointerId);
    }
  }, { passive: false });

  canvas.addEventListener('pointermove', (e) => {
    const { x, y } = toLogical(e.clientX, e.clientY);
    if (pressing) { h.dragCol(colAt(x)); return; }
    if (canHover && e.pointerType === 'mouse' && h.canInput() && overBoard(x, y)) h.hoverCol(colAt(x));
  }, { passive: false });

  canvas.addEventListener('pointerup', (e) => {
    e.preventDefault();
    if (!pressing) return;
    pressing = false;
    const { x, y } = toLogical(e.clientX, e.clientY);
    h.release(overBoard(x, y) ? colAt(x) : null);
  }, { passive: false });

  canvas.addEventListener('pointercancel', () => { if (pressing) { pressing = false; h.release(null); } });

  window.addEventListener('keydown', (e) => {
    if (e.key === 'r' || e.key === 'R') { h.keyRestart(); return; }
    if (h.getPhase() !== 'playing') return;
    if (e.key === 'ArrowLeft') { e.preventDefault(); h.keyMove(-1); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); h.keyMove(1); }
    else if (e.key === 'ArrowDown' || e.key === ' ' || e.key === 'Enter') { e.preventDefault(); h.keyDrop(); }
  });
}

export { canHover };
